import { showModal } from "../../../../../common-deprecated/redux/actions/ModalActions";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../redux/store";
import { formatPrice } from "../../../../../common-deprecated/Globalize";
import { type BuildAndBuyLabelType, getBnbLabel, getBnbOptionalLabel } from "../../utils/labels";
import { MODAL_CONTENT } from "../../../../../common-deprecated/utils/modalConstants";
import { showExternalPriceTax } from "../../utils/settings/bnbSettingUtils";
import { showAemPriceExclVAT } from "../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { useDispatch } from "../../../../../shared-logic/redux/store";
import { sanitize } from "../../../../../common-deprecated/utils/sanitizer";

type DataType = {
    visible: boolean;
    formattedTax: string;
    formattedValueBeforeTax: string;
    showValueBeforeTax: boolean;
    titleLabelKey: BuildAndBuyLabelType;
    fullTaxLabel?: string; // will be defined when co2 value placeholder is not used
    co2TaxLabels?: {
        // will be defined when co2 value placeholder is used
        start: string;
        value: string;
        end: string;
    };
    showCo2Disclaimer: () => void;
};

const useExternalPriceTaxDisclaimer = (): DataType => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    // The BasketExternalPriceTaxDisclaimer uses the tax as returned by the price calculation function, not the one calculated manually in the build (= basket.total.discountTax)
    const tax = useBnbSelector(({ externalPrice }) => externalPrice.calculatedPrice.tax);
    const co2Specs = useBnbSelector(({ externalPrice }) => externalPrice.co2Specs);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const financeOption = useBnbSelector(({ utils }) => utils.financeOption);
    const isExternalPrice = useBnbSelector(({ basket }) => basket.total.isExternalPrice);
    const modelCode = useBnbSelector(({ car }) => car.model.internalCode);
    const valueBeforeTax = useBnbSelector((state) => state.externalPrice.calculatedPrice.totalValueWithoutTax);

    const isExclVat = showAemPriceExclVAT(commonSettings, AemFmComponent.Build, "new", modelCode);

    // get correct price and labels
    const formattedTax = formatPrice(tax, commonSettings.culture.name).replace(" ", "&nbsp;");
    const formattedValueBeforeTax = formatPrice(valueBeforeTax, commonSettings.culture.name).replace(" ", "&nbsp;");
    const labelKey =
        isExclVat && !!getBnbOptionalLabel({ commonSettings, bnbSettings }, "wltpTaxCommercial")
            ? "wltpTaxCommercial"
            : "wltpTax";
    // This makes sure the getLabel method doesn't complain of wltpTax not being filled in.
    const label = getBnbOptionalLabel({ commonSettings, bnbSettings }, labelKey)
        ? getBnbLabel({ commonSettings, bnbSettings }, labelKey)
        : "";
    const hasCO2ValuesInLabel = label.includes("{co2}");

    // title label
    const titleLabelKey =
        isExclVat && !!getBnbOptionalLabel({ commonSettings, bnbSettings }, "wltpTaxTitleCommercial")
            ? "wltpTaxTitleCommercial"
            : "wltpTaxTitle";

    const visible =
        isExternalPrice &&
        financeOption === "cash" &&
        showExternalPriceTax(bnbSettings) &&
        // Also make sure the required labels are filled in, only one is enough to trigger visibilty
        (!!getBnbOptionalLabel({ commonSettings, bnbSettings }, labelKey) ||
            !!getBnbOptionalLabel({ commonSettings, bnbSettings }, titleLabelKey));

    // format label when co2 placeholder is used
    let co2TaxLabels;
    if (visible && label && hasCO2ValuesInLabel) {
        co2TaxLabels = {
            start: label.split("{co2}")[0],
            value: `${co2Specs.value} ${co2Specs.unit}`,
            end: label.split("{co2}")[1],
        };
    }

    const showCo2Disclaimer = (): void => {
        dispatch(
            showModal({
                type: MODAL_CONTENT,
                content: sanitize(getBnbLabel({ commonSettings, bnbSettings }, "co2Disclaimer")),
                useHTML: true,
            }),
        );
    };

    return {
        visible,
        formattedTax,
        formattedValueBeforeTax,
        showValueBeforeTax: valueBeforeTax > 0,
        titleLabelKey,
        fullTaxLabel: label && !hasCO2ValuesInLabel ? label : undefined,
        co2TaxLabels,
        showCo2Disclaimer,
    };
};

export default useExternalPriceTaxDisclaimer;
